import React, {Component} from 'react';
import '../App.css';
import withWorkflow from '../components/withWorkflow';
import {scrollToMessage} from '../util/scrollToMessage';
import RequiredFieldDisclaimer from '../components/RequiredFieldDisclaimer';
import AlertMessage from '../AlertMessage';
import ShippingMethodSelection from '../components/shipping-method/ShippingMethodSelection';
import { isDomesticRequisitionType, isUKOrder, getCustomerCareNumber, isCountryUk, getValidFormattedUKPostalCode } from '../util/gearOrderUtil';

/**
 * Component for the 'ShippingInformation' step of the GEAR order workflow.
 */
class ShippingInformation extends Component {
	constructor(props) {
		super(props);

		const practConfirmStep = this.props.getWorkflowStep('practitioner_confirmation');
		const disableInputs = this.props.userInfo.adminUser !== 'true' && practConfirmStep && practConfirmStep.completed;

		const practitioner = this.props.gearOrder.practitioner || {};
		const office = practitioner.office || {};
		const address = office.address || {};
		const shippingInformation = this.props.gearOrder.shippingInformation || {};
		const address1 = shippingInformation.address1 || address.address1 || '';
		const address2 = shippingInformation.address2 || address.address2 || '';
		const state = shippingInformation.state || address.state || '';
		const instructions = shippingInformation.instructions || '';
		let nyIneligibleProducts = null;
		if( this.props.gearOrder && this.props.gearOrder.requisitions ) {
			nyIneligibleProducts = this.props.gearOrder.requisitions
				.filter(req => req.omsOrderableItemName && req.omsOrderableItemName.endsWith('*'))
				.map(req => req.omsOrderableItemName);
		}
		const hasRestrictedTests = this.hasRestrictedTests( state, nyIneligibleProducts );
		//domestic orders (full option and client bill) are the only ones eligible for expedited shipping
		const domesticRequisitionType = isDomesticRequisitionType(this.props.gearOrder);
		
		// Load existing GEAR shipping information. Fall back to practitioner details if no shipping information exists
		this.state = {
			disableInputs: disableInputs,
			officeCountry : address.country,
			shippingMethod: shippingInformation.shippingMethod || 
					(this.props.gearOrder.shippingMethod ? this.props.gearOrder.shippingMethod 
						: (!isUKOrder(this.props.gearOrder) ? 'FedEx Ground' : isCountryUk(address.country) ? `Royal Mail Tracked (2-3 day delivery)` : `Royal Mail Special Delivery (next working day)`)),
			company: shippingInformation.company || office.name || '',
			attention: shippingInformation.attention || '',
			address1: address1,
			address2: address2,
			city: shippingInformation.city || address.city || '',
			state: state,
			zipCode: shippingInformation.zipCode || address.zipCode || '',
			country: shippingInformation.country || address.country || '',
			
			inputErrors: {
				address1: null,
				city: null,
				state: hasRestrictedTests ? 'RestrictedTests' : null,
				zipCode: null,
				country:null
			},
			showPoBoxWarning: this.hasPOBox(address1, address2),
			nyIneligibleProducts: nyIneligibleProducts,
			instructions: instructions || '',
			domesticRequisitionType: domesticRequisitionType,
			ukOrder: isUKOrder(this.props.gearOrder)
		}
	}

	/**
	 * Update state and validate the required inputs fields when any input changes
	 * @param event The HTML event that triggered this method
	 */
	handleInputChange = (event) => {
		const target = event.target;
		const value = target.type === 'checkbox' ? target.checked : target.value;
		const fieldName = target.name;

		let showPoBoxWarning = this.state.showPoBoxWarning;
		if (fieldName === 'address1' || fieldName === 'address2') {
			const address1 = fieldName === 'address1' ? value : this.state.address1;
			const address2 = fieldName === 'address2' ? value : this.state.address2;
			showPoBoxWarning = this.hasPOBox(address1, address2);
		}

		this.setState({
			[fieldName]: value,
			inputErrors: this.validateForm(fieldName, value),
			showPoBoxWarning: showPoBoxWarning
		});
	}

	/**
	 * Check if either of the address fields contain a P.O. Box.
	 * 
	 * Regex explaination:
	 * 	(P|POST) - Contains the letter 'P' or the word 'POST'
	 * 	[.\s]* - 0 or more periods or spaces
	 * 	(O|OFFICE) - Contains the letter 'O' or the word 'OFFICE'
	 * 	BOX - Contains the word 'BOX'
	 * 	Flags:
	 * 		g - global search (find all matches rather than stopping after the first match)
	 * 		i - Case insensitive
	 * 
	 * @return true if either address is a PO Box, false otherwise
	 */
	hasPOBox = (address1, address2) => {
		const poBoxRegex = /(P|POST)[.\s]*(O|OFFICE)[.\s]*BOX/gi;
		return poBoxRegex.test(address1) || poBoxRegex.test(address2)
	}

	/**
	 * Checks if the state is 'NY' and there are products that are not orderable in New York.
	 * 
	 * @param {String} state The value of the state input
	 * @param {String[]} nyIneligibleProducts The list of selected products that cannot be ordered in New York
	 * @return {boolean}
	 */
	hasRestrictedTests = (state, nyIneligibleProducts) => {
		return state && state.toUpperCase() === 'NY'
			&& nyIneligibleProducts && nyIneligibleProducts.length;
	}

	goBack = (event) => {
		event.preventDefault();
		this.props.updateStep(this.props.gearOrder, 'shipping', 'Back', null);
	}

	/**
	 * Save the shipping information and redirect to the next workflow step
	 */
	handleSubmit = (event) => {
		event.preventDefault();
		if (this.state.disableInputs) {
			// No back-end updates required
			this.props.updateStep(this.props.gearOrder, 'shipping', 'Next', null);
			return;
		}

		// Don't allow double-submit
		if (this.state.submitInProgress) {
			return;
		}

		this.setState({
			inputErrors: this.validateForm()
		}, () => {
			// Confirm all required input are filled before posting the data
			if (Object.values(this.state.inputErrors).every(error => error === null)) {
				// Add gearOrder id and use ...rest syntax to exclude unwanted fields
				let { disableInputs, inputErrors, showPoBoxWarning, nyIneligibleProducts, officeCountry, domesticRequisitionType, ...shippingInfo } = {
					...this.state,
					gearOrderId: this.props.gearOrder.id
				};

				this.setSubmitInProgress(true);

				// Format UK postal code before submitting if country is UK.
				if(isUKOrder(this.props.gearOrder))	{
					if(isCountryUk(this.state?.country)) {
						const formattedPostalCode = getValidFormattedUKPostalCode(this.state.zipCode);
						if(formattedPostalCode !== null) {
							shippingInfo.zipCode = formattedPostalCode;
						}
					} else {
						shippingInfo.zipCode = this.state.zipCode
					}					
				}

				fetch(process.env.REACT_APP_GEAR_REST_URL + '/order/' + this.props.gearOrder.id + '/shippingInfo/', {
					mode: 'cors',
					credentials: 'include',
					method: 'PUT',
					timeout: 1000,
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json'
					},
					body: JSON.stringify(shippingInfo)
				}).then(response => {
					if (response.ok) {
						return response.json();
					} else {
						return Promise.reject(new Error(`An unexpected error has occurred. Please call us at ${getCustomerCareNumber(this.props?.gearOrder)}.`));
					}
				}).then(json => {
					if (json.id) {
						this.props.setGearOrder(json);
						this.props.setDirty(false);
						this.props.updateStep(json, 'shipping', 'Next', null);
					}
				}).catch(error => {
					this.props.showError(error.message);
				}).finally(() => {
					this.setSubmitInProgress(false);
				});

			} else {
				// Scroll to first error
				scrollToMessage();
			}
		});
	}

	setSubmitInProgress = (flag) => {
		this.setState({ submitInProgress: flag });
		this.props.setLoading(flag);
	}

	/**
	 * Validates required fields and returns a map of errors.
	 * If fieldName and value are passed in only that specific field will be validated.
	 * Otherwise, all required fields will be validated.
	 *
	 * @param fieldName (optional) The name of the field to validate
	 * @param value (optional) THe value of the field being validated
	 * @return The updated field errors map
	 */
	validateForm = (fieldName, value) => {
		let errors;
		if (fieldName) {
			errors = {
				...this.state.inputErrors,
				[fieldName]: this.validateField(fieldName, value)
			}
		} else {
			// Call validate on each required field to add/clear error messages
			errors = {
				address1: this.validateField('address1', this.state.address1),
				city: this.validateField('city', this.state.city),
				state: this.validateField('state', this.state.state),
				zipCode: this.validateField('zipCode', this.state.zipCode),
				country: this.validateField('country', this.state.country)
			}
		}

		return errors;
	}

	/**
	 * Validates a required input field.
	 *
	 * @param fieldName The name of the field to validate
	 * @param value THe value of the field being validated
	 * @return The error message if the field is invalid, or null if the field is valid
	 */
	validateField = (fieldName, value) => {
		let error;
		switch (fieldName) {
			case 'address1':
				error = value ? null:
				'Please enter an address';
				break;
			case 'city':
				error = value ? null:
				'Please enter a city';
				break;
			case 'state':
				error = (!this.state.ukOrder && !value) ? 'Please enter a state' : null;
				if (this.hasRestrictedTests(value, this.state.nyIneligibleProducts)) {
					error = 'RestrictedTests'
				}
				break;
			case 'zipCode':
				if (!value) {
					if (this.state.domesticRequisitionType) {
						error = 'Please enter a zip code';
					} else {
						error = 'Please enter a postal code';
					}
				} else if(isUKOrder(this.props.gearOrder) && isCountryUk(this.state?.country) && getValidFormattedUKPostalCode(value) == null) {
					error = 'Please enter a valid postal code';
				} else {
					error = null;
				}
				break;
			case 'country':
				error = value ? null:
				'Please enter a country';
				break;	
			default:
				break;
		}

		return error;
	}

	render() {

		return (<div>
			<div className="row">
				<div className="col-md-12">
					<div>
						<h2>Shipping Information -{' '}
							<small>Select the shipping method and address</small>
						</h2>
					</div>
				</div>
			</div>
			<div className="space-top"></div>
			<AlertMessage display={this.state.disableInputs} />
			{this.state.domesticRequisitionType && (
				<div className="row">
					<div className="col-md-12">
						<h4>Shipping Method</h4>
						<div className="separator-2"></div>
						<ShippingMethodSelection
							gearOrder={this.props.gearOrder}
							shippingMethod={this.state.shippingMethod}
							setShippingMethod={this.handleInputChange}
							isDisabledInputs={this.state.disableInputs}
							userInfo={this.props.userInfo}
							/>
					</div>
				</div>
			)}		
			<div>
				<div>
					<h4>Shipping Address</h4>
					<div className="separator-2"></div>
					<span>
						Please verify the address your packs will be sent to and make any necessary changes. If you would like to make permanent changes to this information, please contact&nbsp;
						<a href="/mygdx/contact">Customer Support</a>.
					</span>
					<div className="space-top"></div>
				</div>
				<div className="row">
					<div className="form-group col-sm-6 ">
						<label className="control-label" htmlFor="company">Company</label>
						<input type="text" className="form-control" name="company" id="company" maxLength="128" value={this.state.company} onChange={this.handleInputChange} disabled={this.state.disableInputs}/>
					</div>
					<div className="form-group col-sm-6">
						<label className="control-label" htmlFor="attention">Attention</label>
						<input type="text" className="form-control" name="attention" id="attention" maxLength="128" value={this.state.attention} onChange={this.handleInputChange} disabled={this.state.disableInputs}/>
					</div>
				</div>
				<div className="row">
					<div className={'form-group col-md-6 has-feedback' + (
						this.state.inputErrors.address1 ? ' has-error' : '')}>
						<label className="control-label" htmlFor="address1">Address 1<span className="text-danger">*</span>
						</label>
						<input type="text" className="form-control" name="address1" id="address1" maxLength="128" value={this.state.address1} onChange={this.handleInputChange} disabled={this.state.disableInputs}/> {this.state.inputErrors.address1 && <span className="validation-message text-danger">{this.state.inputErrors.address1}</span>}
					</div>
					<div className="form-group col-md-6">
						<label className="control-label" htmlFor="address2">Address 2</label>
						<input type="text" className="form-control" name="address2" id="address2" maxLength="128" value={this.state.address2} onChange={this.handleInputChange} disabled={this.state.disableInputs}/>
					</div>
					{this.state.showPoBoxWarning && 
						<div className='form-group col-12'>
							<AlertMessage type='warning'>We are unable to ship Collection Packs to a P.O. Box. If this address is a P.O. Box please change it or contact us as at {getCustomerCareNumber(this.props?.gearOrder)} for assistance.</AlertMessage>
						</div>
					}
				</div>

				<div className="row">
					<div className={'form-group col-lg-4 col-sm-3' + (
						this.state.inputErrors.city ? ' has-error' : '')}>
						<label htmlFor="city" className="control-label">City
							<span className="text-required">*</span>
						</label>
						<input id="city" name="city" className="form-control" type="text" maxLength="64" value={this.state.city} onChange={this.handleInputChange} disabled={this.state.disableInputs}/> {this.state.inputErrors.city && <span className="validation-message text-danger">{this.state.inputErrors.city}</span>}
					</div>

					{!this.state.ukOrder && (
						<div className={'form-group col-lg-2 col-sm-3' + (
							this.state.inputErrors.state ? ' has-error' : '')}>
							<label htmlFor="state" className="control-label">State{!this.state.ukOrder && <span className="text-required">*</span>}
							</label>
							<input id="state" name="state" className="form-control" type="text" maxLength="32" value={this.state.state} onChange={this.handleInputChange} disabled={this.state.disableInputs} />
						{(this.state.inputErrors.state !== 'RestrictedTests') && <span className="validation-message text-danger">{this.state.inputErrors.state}</span>}
						</div>
					)}

					<div className={'form-group col-lg-2 col-sm-3' + (
						this.state.inputErrors.zipCode ? ' has-error' : '')}>
						<label htmlFor="zipCode" className="control-label">{this.state.domesticRequisitionType ? 'Zip Code' : 'Postal Code'}<span className="text-required">*</span>
						</label>
						<input id="zipCode" name="zipCode" className="form-control" type="text" value={this.state.zipCode} size="10" maxLength="10" onChange={this.handleInputChange} disabled={this.state.disableInputs}/> {this.state.inputErrors.zipCode && <span className="validation-message text-danger">{this.state.inputErrors.zipCode}</span>}
					</div>
					
					<div className={'form-group col-md-4 col-sm-3' + (
							this.state.inputErrors.country ? ' has-error' : '')}>
							<label htmlFor="country" className="control-label">Country<span className="text-required">*</span>
							</label>
							<input id="country" name="country" className="form-control" type="text" value={this.state.country} onChange={this.handleInputChange} disabled={this.state.disableInputs}/> {this.state.inputErrors.country && <span className="validation-message text-danger">{this.state.inputErrors.country}</span>}
					</div>

					{(this.state.inputErrors.state === 'RestrictedTests') && 
						<div className='form-group col-12 validation-message text-danger'>
							<AlertMessage type='danger'>
									One or more of the products you have selected cannot be ordered in the state of New York.
									Please ship to a different location or remove these products from your order.
								<ul>
									{this.state.nyIneligibleProducts.map( (prod, index) => <li key={index}>{prod}</li>)}
								</ul>

							</AlertMessage>
						</div>
					}
				</div>
			</div>
			<div className="space-top"></div>
			<div className="form-group">
				<h4>Order Instructions <small>(Optional)</small></h4>
				<div className="separator-2"></div>
				<span>
				These instructions will be processed by Genova's Customer Care team and may delay processing. 
				</span>
			<div className="space-top"></div>
				<textarea name="instructions" className="form-control" maxLength="512" rows="3" defaultValue={this.state.instructions} onChange={this.handleInputChange} disabled={this.state.disableInputs}></textarea>
				<span className="pull-right"></span>
			</div>
			<RequiredFieldDisclaimer />
			<div className="row">
				<div className={'col-md-12 col-12' + (
					this.state.disableInputs ? ' sticky-bottom' : '')}>
					<button type="button" className="btn btn-white rounded-pill btn-sm margin-clear" onClick={this.goBack}>
						<i className="pe-2 fa fa-arrow-circle-left"></i>
						Back
					</button>
					&nbsp;
					<button type="submit" form="panels-form" className="btn btn-primary rounded-pill btn-sm margin-clear pull-right" onClick={this.handleSubmit}>
						Next
						<i className="ps-2 fa fa-arrow-circle-right"></i>
					</button>
				</div>
			</div>
		</div>)
	}
}
// wrap the component in the workflow HOC to get access to common workflow logic
export default withWorkflow(ShippingInformation);